// Where things live in the Email module, and where its former URLs land.
// Pure: no React, no runtime import — `scripts/tests/email-module.test.mjs`
// runs it under node:test directly.
//
//   /projects/:slug/email/campaigns
//   /projects/:slug/email/outreach
//   /projects/:slug/email/campaigns/:id/{sequence,leads,launch,performance}
//   /projects/:slug/email/campaigns/:id/review   (a locked pilot only)
//   …?lead=<membership id> opens that lead's drawer wherever it is read.

export type WorkspaceTab = "sequence" | "leads" | "launch" | "performance" | "review";

export const WORKSPACE_TABS: { key: WorkspaceTab; label: string }[] = [
  { key: "sequence", label: "Sequence" },
  { key: "leads", label: "Leads" },
  { key: "launch", label: "Launch" },
  { key: "performance", label: "Performance" },
];

/** A locked pilot's review: shown first, and only on a pilot. */
export const REVIEW_TAB: { key: WorkspaceTab; label: string } = { key: "review", label: "Review" };

type Params = Record<string, string | number | null | undefined>;

function withParams(path: string, params?: Params): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === null || value === undefined || value === "") continue;
    query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}

export function emailBase(slug: string): string {
  return `/projects/${slug}/email`;
}

export function campaignsPath(slug: string, params?: Params): string {
  return withParams(`${emailBase(slug)}/campaigns`, params);
}

export function outreachPath(slug: string, params?: Params): string {
  return withParams(`${emailBase(slug)}/outreach`, params);
}

/** A campaign's workspace tab — always in the campaign's own project. */
export function campaignPath(slug: string, id: number, tab: WorkspaceTab = "sequence", params?: Params): string {
  return withParams(`${emailBase(slug)}/campaigns/${id}/${tab}`, params);
}

/** The workspace tab a path shows, from its last segment. */
export function workspaceTabOf(pathname: string): WorkspaceTab {
  const last = pathname.replace(/\/+$/, "").split("/").pop() ?? "";
  if (last === REVIEW_TAB.key) return REVIEW_TAB.key;
  return (WORKSPACE_TABS.find((t) => t.key === last)?.key ?? "sequence") as WorkspaceTab;
}

function positiveId(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return id > 0 ? id : null;
}

/**
 * Where a former Email URL lands, relative to `/projects/:slug/email/`:
 * - `?campaign=ID` (with or without `tab=contacts`) → that campaign's Leads;
 * - `?tab=templates`, `?tab=contacts`, or nothing → Campaigns.
 * A `lead` parameter rides along to open its drawer.
 */
export function legacyEmailTarget(search: string): string {
  const params = new URLSearchParams(search);
  const campaign = positiveId(params.get("campaign"));
  const lead = positiveId(params.get("lead"));
  const tail = lead ? `?lead=${lead}` : "";
  if (campaign) return `campaigns/${campaign}/leads${tail}`;
  return `campaigns${tail}`;
}

/**
 * The workspace's bare URL (the former campaign page) → its Sequence, keeping
 * the view (`?view=canvas`; the old `kanban` is the Canvas now) and a lead.
 */
export function workspaceIndexTarget(search: string): string {
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();
  const view = params.get("view");
  if (view === "canvas" || view === "kanban") next.set("view", "canvas");
  const lead = positiveId(params.get("lead"));
  if (lead) next.set("lead", String(lead));
  const text = next.toString();
  return text ? `sequence?${text}` : "sequence";
}
